import React from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { Dialog } from '@bb-ui/react-library/dist/components/Dialog';
import { DialogTitle } from '@bb-ui/react-library/dist/components/DialogTitle';
import { DialogContent } from '@bb-ui/react-library/dist/components/DialogContent';
import { DialogActions } from '@bb-ui/react-library/dist/components/DialogActions';
import {
    DefaultButton,
    PrimaryButton,
} from '@bb-ui/react-library/dist/components/Button';
import { TextField } from '@bb-ui/react-library/dist/components/TextField';
import { Banner } from '@bb-ui/react-library/dist/components/Banner';
import { CircularProgress } from '@bb-ui/react-library/dist/components/Progress';
import { UpdateState } from '../identityProviderManagement.types';
import { IdentityProviderRow, nameKey } from '../identityProviderRows';
import { useIdpConnection } from '../hooks/useIdpConnection';
import { SetupGuide } from '../SetupGuide/SetupGuide';
import { useDialogTabWrap } from '../../../../hooks/useDialogTabWrap';
import { useStyles } from '../Authentication.styles';
import { TransText } from '../transHelper';
import { useI18nContext } from '../../../I18nProvider/I18nProvider';
import { getFormatLocale } from '../../../../utilities/localization';

interface Props {
    open: boolean;
    onClose: () => void;
    /** The row being edited. Its values seed the form. */
    identityProvider?: IdentityProviderRow;
    /** Called after a successful save, so the list can pick up the new values. */
    onSaved?: () => void;
    /** Names the provider may not be renamed to, in `nameKey` form. */
    takenNames?: ReadonlySet<string>;
}

interface Fields {
    displayName: string;
    metadataUrl: string;
    nameIdentifier: string;
}

const EDIT = 'settings.authentication.edit';

const NO_NAMES: ReadonlySet<string> = new Set();

const REASON_COPY: Record<string, string> = {
    OWN_CONNECTION: `${EDIT}.ownIdp`,
    LEGACY_NO_METADATA_URL: `${EDIT}.legacyNoMetadataUrl`,
    CHECK_UNAVAILABLE: `${EDIT}.checkUnavailable`,
    CLONED: `${EDIT}.cloned`,
    METADATA_OR_ATTRIBUTE_INVALID: `${EDIT}.saveFailedMetadataOrAttribute`,
};

// Matches "https://idp.example.edu/md.xml"; not "https:test" or "https:/x",
// which URL() would otherwise accept.
const HTTPS_PREFIX = /^https:\/\//;

const isHttpsUrl = (value: string) => {
    try {
        return HTTPS_PREFIX.test(value) && new URL(value).hostname !== '';
    } catch {
        return false;
    }
};

/**
 * Edit an existing identity provider: the same fields the Add wizard's Configure
 * step collects, pre-filled, alongside the connection details the admin needs in
 * their own console.
 */
export const EditIdentityProviderDialog: React.FunctionComponent<Props> = ({
    open,
    onClose,
    identityProvider,
    onSaved,
    takenNames = NO_NAMES,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    useDialogTabWrap(open);
    const { userSelectedLang } = useI18nContext();
    const locale = getFormatLocale(userSelectedLang);
    const update = useIdpConnection(identityProvider?.connectionName, open);

    const [fields, setFields] = React.useState<Fields>({
        displayName: '',
        metadataUrl: '',
        nameIdentifier: '',
    });
    const [showErrors, setShowErrors] = React.useState(false);

    /**
     * Re-seed from the row on every opening: the component stays mounted, so
     * without this an admin who edits one provider, cancels, then opens another
     * would edit the second under the first one's values, and save them.
     */
    const wasOpen = React.useRef(false);
    React.useEffect(() => {
        if (open && !wasOpen.current) {
            setShowErrors(false);
            setFields({ displayName: '', metadataUrl: '', nameIdentifier: '' });
            update.reset();
        }
        wasOpen.current = open;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, identityProvider]);

    /**
     * Seed once the connection arrives, keyed on the fetched object rather than
     * `open`: the read is asynchronous, so seeding at open time fills the form
     * with blanks and leaves them there.
     */
    React.useEffect(() => {
        if (!update.connection) return;
        setFields({
            displayName: update.connection.displayName ?? '',
            metadataUrl: update.connection.metadataUrl ?? '',
            nameIdentifier: update.connection.mapping?.user_id ?? '',
        });
    }, [update.connection]);

    React.useEffect(() => {
        if (update.state === UpdateState.SAVED) {
            onSaved?.();
            onClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [update.state]);

    const errors = React.useMemo(() => {
        const required = (value: string) =>
            value.trim()
                ? undefined
                : t('settings.authentication.wizard.required');
        const metadataUrlError = () => {
            const trimmed = fields.metadataUrl.trim();
            if (!trimmed) return t('settings.authentication.wizard.required');
            return isHttpsUrl(trimmed)
                ? undefined
                : t('settings.authentication.wizard.invalidUrl');
        };
        const displayNameError = () =>
            required(fields.displayName) ??
            (takenNames.has(nameKey(fields.displayName))
                ? t('settings.authentication.wizard.nameTaken')
                : undefined);

        return {
            displayName: displayNameError(),
            metadataUrl: metadataUrlError(),
            nameIdentifier: required(fields.nameIdentifier),
        };
    }, [fields, t, takenNames]);

    const firstInvalid = (
        ['displayName', 'metadataUrl', 'nameIdentifier'] as const
    ).find(k => errors[k]);

    const fieldRefs = {
        displayName: React.useRef<HTMLInputElement>(null),
        metadataUrl: React.useRef<HTMLInputElement>(null),
        nameIdentifier: React.useRef<HTMLInputElement>(null),
    };

    const handleSave = () => {
        setShowErrors(true);
        if (firstInvalid) {
            fieldRefs[firstInvalid].current?.focus();
            return;
        }
        update.save({
            displayName: fields.displayName,
            metadataUrl: fields.metadataUrl,
            mapping: { user_id: fields.nameIdentifier },
        });
    };

    const set =
        (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement>) =>
            setFields(f => ({ ...f, [key]: e.target.value }));

    const titleId = 'idp-edit-dialog-title';
    const notEditableId = 'idp-edit-dialog-not-editable';
    const saving = update.state === UpdateState.SAVING;
    // The re-read is what makes the stored values authoritative, so a failed
    // one must not be reported as though they were read.
    const unknownOutcomeCopy = update.loadFailed
        ? `${EDIT}.outcomeUnknownUnread`
        : `${EDIT}.outcomeUnknown`;
    const readOnly = update.connection?.editable === false;
    const reason = update.connection?.notEditableReason;
    const hasValues = !!update.connection && reason !== 'CLONED';

    const reasonCopy = (code: string | undefined, fallback: string) => (
        <TransText
            text={t((code && REASON_COPY[code]) || fallback)}
            tags={[<span key="english" lang="en" />]}
        />
    );

    const expiresAt = update.connection?.certificateExpiresAt;
    const hasExpired = !!expiresAt && new Date(expiresAt) < new Date();

    const field = (
        key: keyof Fields,
        labelKey: string,
        hint: React.ReactNode,
        warning?: string,
    ) => (
        <TextField
            id={`edit-idp-${key}`}
            data-testid={`edit-idp-${key}`}
            inputRef={fieldRefs[key]}
            label={
                <TransText
                    text={t(`settings.authentication.wizard.${labelKey}`)}
                    tags={[<span key="english" lang="en" />]}
                />
            }
            value={fields[key]}
            onChange={set(key)}
            InputProps={readOnly ? { readOnly: true } : undefined}
            error={!readOnly && showErrors && !!errors[key]}
            helperText={
                <>
                    {showErrors && errors[key] ? errors[key] : hint}
                    {warning && (
                        <span
                            className={classes.fieldWarning}
                            data-testid={`edit-idp-${key}-warning`}
                        >
                            {warning}
                        </span>
                    )}
                </>
            }
            required
            fullWidth
        />
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            classes={{ paper: classes.wizardPaper }}
            aria-labelledby={titleId}
            aria-describedby={readOnly ? notEditableId : undefined}
            data-testid="editIdentityProviderDialog"
        >
            <DialogTitle
                id={titleId}
                onClose={onClose}
                closeLabel={t('general.close')}
            >
                {t('settings.authentication.edit.title', {
                    name: identityProvider?.displayName ?? '',
                })}
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                {update.state === UpdateState.FAILED && (
                    <Banner
                        severity={update.outcomeUnknown ? 'warning' : 'error'}
                        aria-live="polite"
                        className={classes.banner}
                        data-testid="editSaveError"
                    >
                        {update.outcomeUnknown
                            ? t(unknownOutcomeCopy)
                            : reasonCopy(
                                  update.failureReason,
                                  `${EDIT}.saveFailed`,
                              )}
                    </Banner>
                )}

                {readOnly && (
                    <Banner
                        id={notEditableId}
                        severity="info"
                        className={classnames(
                            classes.banner,
                            classes.notEditableBanner,
                        )}
                        data-testid="editNotEditable"
                    >
                        {reasonCopy(reason, `${EDIT}.notEditable`)}
                    </Banner>
                )}

                {update.loadFailed && (
                    <Banner
                        severity="error"
                        className={classes.banner}
                        data-testid="editLoadError"
                    >
                        {t('settings.authentication.edit.loadFailed')}
                    </Banner>
                )}

                {!update.connection && !update.loadFailed && (
                    <div
                        className={classes.statusCell}
                        data-testid="editLoading"
                    >
                        <CircularProgress
                            size={18}
                            ariaLabel={t(`${EDIT}.loading`)}
                        />
                        {t(`${EDIT}.loading`)}
                    </div>
                )}

                {hasValues && (
                    <div className={classes.fieldColumn}>
                        {field(
                            'displayName',
                            'displayName',
                            t('settings.authentication.wizard.displayNameHint'),
                        )}
                        {field(
                            'metadataUrl',
                            'metadataUrl',
                            // No type is recorded for an existing provider, so
                            // there is no console-specific path to point at.
                            // prettier-ignore
                            <Trans i18nKey="settings.authentication.wizard.metadataHint">
                                Your identity provider's published <span lang="en">SAML</span> 2.0 metadata URL (must be publicly accessible).
                            </Trans>,
                        )}
                        {field(
                            'nameIdentifier',
                            'nameIdentifier',
                            // prettier-ignore
                            <Trans i18nKey="settings.authentication.wizard.nameIdentifierHint">
                                The <span lang="en">SAML</span> attribute <span lang="en">Illuminate</span> should use to match this user's account.
                            </Trans>,
                            readOnly
                                ? undefined
                                : t(`${EDIT}.nameIdentifierWarning`),
                        )}
                    </div>
                )}

                {expiresAt && (
                    <div
                        className={classes.certificateExpiry}
                        data-testid="editCertificateExpires"
                    >
                        {t(
                            hasExpired
                                ? `${EDIT}.certificateExpired`
                                : `${EDIT}.certificateExpires`,
                        )}{' '}
                        <time
                            dateTime={expiresAt}
                            className={classnames({
                                [classes.certificateExpiryExpired]: hasExpired,
                                [classes.certificateExpiryValid]: !hasExpired,
                            })}
                            data-testid="editCertificateExpiresDate"
                        >
                            {new Date(expiresAt).toLocaleDateString(locale)}
                        </time>
                    </div>
                )}

                <SetupGuide
                    type="OTHER"
                    selectable
                    connectionName={identityProvider?.connectionName}
                />
            </DialogContent>

            <DialogActions>
                <DefaultButton onClick={onClose} data-testid="editCancel">
                    {readOnly ? t(`${EDIT}.close`) : t(`${EDIT}.cancel`)}
                </DefaultButton>
                {!readOnly && (
                    <PrimaryButton
                        onClick={handleSave}
                        disabled={saving || !update.connection}
                        data-testid="editSave"
                    >
                        {saving ? (
                            <CircularProgress
                                size={18}
                                ariaLabel={t(`${EDIT}.saving`)}
                            />
                        ) : (
                            t(`${EDIT}.save`)
                        )}
                    </PrimaryButton>
                )}
            </DialogActions>
        </Dialog>
    );
};
